import React, { useState } from "react";
import { formatDistance } from 'date-fns';
import { it } from 'date-fns/locale';
import { Card, Typography, Button, Tag, Tooltip, Progress, Alert } from 'antd';
import {
  ReloadOutlined,
  GithubOutlined,
  CloudOutlined,
  BranchesOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from '../../api_config.json';
import './ProjectBuild.css';

const { Text } = Typography;

// Helper component: Retry button
function Retry({ id }) {
  const [retrying, setRetrying] = useState(false);
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  const retryBuild = async () => {
    setRetrying(true);

    try {
      const token = await getTokenSilently();

      await fetch(`${config.ciUnified[isProd ? 'prod' : 'dev']}/builds/${id}/retry`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => setRetrying(false), 2000);
    }
  };

  return (
    <Button
      type="primary"
      size="small"
      icon={<ReloadOutlined spin={retrying} />}
      onClick={(e) => {
        e.stopPropagation();
        retryBuild();
      }}
      className="build-card__retry-btn"
    >
      Riprova
    </Button>
  );
}

// Helper component: Status badge with icon and color
const StatusBadge = ({ status }) => {
  const statusConfig = {
    succeeded: {
      color: 'success',
      icon: <CheckCircleOutlined />,
      text: 'Completato'
    },
    failed: {
      color: 'error',
      icon: <CloseCircleOutlined />,
      text: 'Fallito'
    },
    in_progress: {
      color: 'warning',
      icon: <SyncOutlined spin />,
      text: 'In corso'
    },
    stopped: {
      color: 'default',
      icon: <StopOutlined />,
      text: 'Fermato'
    }
  };

  const config = statusConfig[status] || statusConfig.stopped;

  return (
    <Tag color={config.color} icon={config.icon} className="build-card__status-tag">
      {config.text}
    </Tag>
  );
};

// Helper component: Build progress bar
const BuildProgress = ({ currentStep, startedAt, progressPercent }) => {
  const elapsed = startedAt
    ? formatDistance(new Date(startedAt), new Date(), { locale: it })
    : null;

  return (
    <div className="build-card__progress">
      <Progress
        percent={progressPercent || 50}
        status="active"
        strokeColor={{
          '0%': '#108ee9',
          '100%': '#87d068',
        }}
        showInfo={true}
      />
      <div className="build-card__progress-info">
        <Text type="secondary" className="build-card__progress-step">
          <SyncOutlined spin /> {currentStep || 'In esecuzione...'}
        </Text>
        {elapsed && (
          <Text type="secondary" className="build-card__progress-elapsed">
            <ClockCircleOutlined /> Trascorso: {elapsed}
          </Text>
        )}
      </div>
    </div>
  );
};

// Helper component: Error section for failed builds
const ErrorSection = ({ failedStep, errorSummary }) => {
  if (!failedStep && !errorSummary) return null;

  return (
    <Alert
      type="error"
      className="build-card__error"
      showIcon
      icon={<ExclamationCircleOutlined />}
      message={
        <div className="build-card__error-content">
          {failedStep && (
            <div className="build-card__error-step">
              Fallito in: <strong>{failedStep}</strong>
            </div>
          )}
          {errorSummary && (
            <div className="build-card__error-message">
              {errorSummary.substring(0, 150)}{errorSummary.length > 150 ? '...' : ''}
            </div>
          )}
        </div>
      }
    />
  );
};

// Helper component: Provider icon
const ProviderIcon = ({ provider }) => {
  if (provider === 'github_actions') {
    return (
      <Tooltip title="GitHub Actions">
        <GithubOutlined className="build-card__provider-icon" />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="AWS CodeBuild">
      <CloudOutlined className="build-card__provider-icon" />
    </Tooltip>
  );
};

const ProjectBuild = ({
  projectName,
  hash,
  id,
  buildStatus,
  executionTime,
  startedAt,
  endTime,
  provider,
  branch,
  errorSummary,
  failedStep,
  currentStep,
  commitMessage,
  commitAuthor,
  commitUrl,
  prNumber,
  prUrl,
  progressPercent,
  onShowDetails,
  triggerType,
}) => {
  const handleCardClick = () => {
    if (onShowDetails) {
      onShowDetails();
    }
  };

  const isInProgress = buildStatus === 'in_progress';
  const isFailed = buildStatus === 'failed';
  const isWorkflow = triggerType === 'workflow';

  return (
    <Card
      className={`build-card build-card--${buildStatus}${isWorkflow ? ' build-card--workflow' : ''}`}
      hoverable
      onClick={handleCardClick}
    >
      {/* Header: Provider + Title + Status + Retry */}
      <div className="build-card__header">
        <div className="build-card__header-left">
          {provider && <ProviderIcon provider={provider} />}
          <Text strong className="build-card__title">{projectName}</Text>
        </div>
        <div className="build-card__header-right">
          {isWorkflow && (
            <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>
              Preview
            </Tag>
          )}
          <StatusBadge status={buildStatus} />
          {(buildStatus === 'failed' || buildStatus === 'stopped') && (
            <Retry id={id} />
          )}
        </div>
      </div>

      {/* Git Info: Branch + SHA + PR tags */}
      {(branch || hash || prNumber) && (
        <div className="build-card__git-info">
          {branch && (
            <Tag icon={<BranchesOutlined />} className="build-card__branch-tag">
              {branch}
            </Tag>
          )}
          {hash && (
            <Tag
              className={`build-card__sha-tag ${commitUrl ? 'build-card__sha-tag--clickable' : ''}`}
              onClick={commitUrl ? (e) => {
                e.stopPropagation();
                window.open(commitUrl, '_blank', 'noopener,noreferrer');
              } : undefined}
            >
              {hash.substring(0, 7)}
            </Tag>
          )}
          {prNumber && prUrl && (
            <Tag
              color="purple"
              className="build-card__pr-tag"
              onClick={(e) => {
                e.stopPropagation();
                window.open(prUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              PR #{prNumber}
            </Tag>
          )}
        </div>
      )}

      {/* Commit: Message + Author */}
      {(commitMessage || commitAuthor) && (
        <div className="build-card__commit">
          {commitMessage && (
            <Text className="build-card__commit-message">
              "{commitMessage.split('\n')[0].substring(0, 80)}{commitMessage.length > 80 ? '...' : ''}"
            </Text>
          )}
          {commitAuthor && (
            <Text type="secondary" className="build-card__commit-author">
              <UserOutlined /> di @{commitAuthor.replace(/^@/, '')}
            </Text>
          )}
        </div>
      )}

      {/* Progress Section (only for in_progress with historical data) */}
      {isInProgress && progressPercent != null && (
        <BuildProgress
          currentStep={currentStep}
          startedAt={startedAt}
          progressPercent={progressPercent}
        />
      )}

      {/* Simple progress indicator when no historical data */}
      {isInProgress && progressPercent == null && (
        <div className="build-card__progress-simple">
          <Text type="secondary">
            <SyncOutlined spin /> {currentStep || 'In esecuzione...'}
          </Text>
        </div>
      )}

      {/* Error Section (only for failed) */}
      {isFailed && (
        <ErrorSection
          failedStep={failedStep}
          errorSummary={errorSummary}
        />
      )}

      {/* Footer: Timestamp + Duration */}
      <div className="build-card__footer">
        <Text type="secondary" className="build-card__timestamp">
          <ClockCircleOutlined />
          {isInProgress
            ? (startedAt && `Iniziato ${formatDistance(new Date(startedAt), new Date(), { addSuffix: true, locale: it })}`)
            : (typeof endTime === 'string' && `Completato ${formatDistance(new Date(endTime), new Date(), { addSuffix: true, locale: it })}`)
          }
        </Text>
        {!isInProgress && executionTime != null && (
          <Text type="secondary" className="build-card__duration">
            Durata: {executionTime} min
          </Text>
        )}
      </div>
    </Card>
  );
};

export default ProjectBuild;
